'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';

interface PartnerShowcaseProps {
  isHydrated: boolean;
}

interface PartnerLogo {
  name: string;
  imagePath: string;
  alt: string;
  brandSlug: string;
}

const partnerLogos: PartnerLogo[] = [
  {
    name: 'SB Motors',
    imagePath: '/assets/images/SB_Motors-1763959693437.png',
    alt: 'SB Motors logo - circular white logo on blue background with stylized SB monogram representing professional automotive brand',
    brandSlug: 'SB+Motors',
  },
  {
    name: 'Moris',
    imagePath: '/assets/images/Moris-1763959745041.png',
    alt: 'Moris logo - modern lifestyle brand featuring distinctive red and black M design with cursive tagline representing quality home furnishings',
    brandSlug: 'Moris',
  },
  {
    name: 'Mitsubishi Electric',
    imagePath: '/assets/images/Mitsubishi-1763959816610.png',
    alt: 'Mitsubishi Electric US Inc logo - iconic red three-diamond logo with company name and Changes for the Better tagline representing global technology leader',
    brandSlug: 'Mitsubishi',
  },
  {
    name: 'Blain Hydraulics',
    imagePath: '/assets/images/Blain-1763959852611.png',
    alt: 'Blain Hydraulics logo - professional shield-shaped brand design featuring navy blue BLAIN text with diamond geometric symbol and curved HYDRAULICS text on cream background representing hydraulics expertise',
    brandSlug: 'Blain',
  },
  {
    name: 'Pizzato',
    imagePath: '/assets/images/pizzato-1763959896263.png',
    alt: 'Pizzato logo - distinctive red diamond-shaped geometric symbol with modern pizzato typography in burgundy color representing professional automation and safety component manufacturer',
    brandSlug: 'Pizzato',
  },
  {
    name: 'VEGA',
    imagePath: '/assets/images/VEGA-1763959942169.png',
    alt: 'VEGA logo - modern black typography with Italian Style for Lifts tagline representing sophisticated elevator components',
    brandSlug: 'VEGA',
  },
  {
    name: 'Honeywell Building Automation',
    imagePath: '/assets/images/Honeywell-1763960123936.png',
    alt: 'Honeywell Building Automation logo - clean professional brand logo with bold red Honeywell text and blue Building Automation subtitle representing trusted industrial automation and building management systems',
    brandSlug: 'Honeywell',
  },
  {
    name: 'TAL Elevator Controls',
    imagePath: '/assets/images/TAL-1763960205370.png',
    alt: 'TAL Elevator Controls logo - professional brand featuring green 3D geometric cube with circuit-board pattern and bold TAL typography representing precision engineering and technical expertise in elevator control systems',
    brandSlug: 'TAL',
  },
  {
    name: 'WURTEC',
    imagePath: '/assets/images/Wurtec-1763960226833.png',
    alt: 'WURTEC logo - bold white sans-serif typography on bright red rectangular background representing industrial strength, reliability and technical engineering excellence in industrial components',
    brandSlug: 'Wurtec',
  },
  {
    name: 'EMI/PORTA',
    imagePath: '/assets/images/EMIPORTA-1778049121524.jpg',
    alt: 'EMI PORTA logo - professional elevator components brand representing quality manufacturing and reliable elevator parts supply',
    brandSlug: 'EMI',
  },
  {
    name: 'Woodfold',
    imagePath: '/assets/images/Woodfold-1778049121614.jpg',
    alt: 'Woodfold logo - established brand representing quality folding doors and elevator cab enclosure products',
    brandSlug: 'Woodfold',
  },
];

const PartnerShowcase = ({ isHydrated }: PartnerShowcaseProps) => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    if (!isHydrated) return;

    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isHydrated]);

  const parallaxOffset = isHydrated ? scrollY * 0.15 : 0;

  return (
    <section className="relative py-7 lg:py-[2.5rem] bg-secondary overflow-hidden">
      {/* Subtle Background Pattern */}
      <div
        className="absolute inset-0 opacity-5 bg-gradient-to-b from-primary/10 to-transparent pointer-events-none"
        style={{
          transform: isHydrated ? `translateY(${parallaxOffset}px)` : 'translateY(0)',
          transition: 'transform 0.1s ease-out',
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-16 lg:mb-20">
          <div className="inline-flex items-center gap-2 bg-red-600 text-white text-xs font-bold uppercase tracking-widest px-4 py-1.5 mb-4">
            Trusted Partnerships
          </div>
          <h2 className="font-headline font-bold text-3xl lg:text-4xl text-primary mb-4">
            Authorized Distributor for Industry Leaders
          </h2>
          <p className="font-body text-base lg:text-lg text-text-secondary max-w-2xl mx-auto">
            We stock to sell, not to make you wait. Click any brand to browse their parts directly.
          </p>
        </div>

        {/* Partner Logos Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-8 lg:gap-12 items-center">
          {partnerLogos.map((partner) => (
            <Link
              key={partner.name}
              href={`/products?brand=${encodeURIComponent(partner.brandSlug)}`}
              className="flex items-center justify-center p-6 bg-background/50 border border-border/30 transition-all duration-medium hover:border-red-600/50 hover:bg-background/80 hover:-translate-y-1 group cursor-pointer"
              title={`Browse ${partner.name} parts`}
            >
              <div className="relative w-full h-24">
                <AppImage
                  src={partner.imagePath}
                  alt={partner.alt}
                  width={200}
                  height={96}
                  className="object-contain transition-all duration-medium w-full h-full group-hover:opacity-90"
                  sizes="(max-width: 768px) 50vw, (max-width: 1024px) 33vw, 25vw"
                />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default PartnerShowcase;